import React, { useContext, useState, useEffect } from 'react';
import AdminSidebar from '../../../components/AdminSidebar/AdminSidebar';
import { AuthContext } from '../../../context/AuthContext';
import { FaUser, FaEnvelope, FaShieldAlt, FaDiscord, FaLock, FaTrashAlt, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import './AdminProfile.css';

// Permite configurar la API en desarrollo con REACT_APP_API_BASE_URL, por defecto usa "/api"
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

const AdminProfile = () => {
  const { user, authToken, logout } = useContext(AuthContext);
  
  const [username, setUsername] = useState(user?.username || '');
  const [email, setEmail] = useState(user?.email || '');
  const [discordId, setDiscordId] = useState(user?.discord_id || '');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // Intentamos obtener la información actualizada del usuario desde la base de datos
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.id || !authToken) return;
      try {
        const response = await fetch(`${API_BASE_URL}/users`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (response.ok) {
          const usersList = await response.json();
          const currentUser = usersList.find(u => u.id === user.id);
          if (currentUser) {
            setEmail(currentUser.email || '');
            setDiscordId(currentUser.discord_id || '');
            setUsername(currentUser.username || '');
          }
        }
      } catch (err) {
        console.error('Error fetching fresh user data:', err);
      }
    };
    fetchUserData();
  }, [user?.id, authToken]);

  const handleChangeProfile = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!username.trim()) {
      setError('El nombre de usuario es obligatorio.');
      return;
    }
    if (!email.trim()) {
      setError('El email es obligatorio.');
      return;
    }
    if (password && password.length < 6) {
      setError('La nueva contraseña debe tener al menos 6 caracteres.');
      return;
    }
    if (password && password !== password2) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    try {
      setLoading(true);
      const body = {
        username: username,
        email: email,
        discord_id: discordId,
        role: user.role,
        status: user.status || 'active',
      };
      if (password) {
        body.password = password;
      }
      const response = await fetch(`${API_BASE_URL}/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Error al actualizar el perfil.');
      }
      setPassword('');
      setPassword2('');
      setSuccess('Perfil actualizado correctamente.');
    } catch (err) {
      setError('Error al actualizar el perfil: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('¿Seguro que quieres borrar tu cuenta? Esta acción es irreversible y perderás todos tus datos.')) return;
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/users/${user.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (!response.ok) {
        throw new Error('Error al borrar la cuenta.');
      }
      alert('Cuenta borrada correctamente.');
      logout();
    } catch (err) {
      setError('Error al borrar la cuenta: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-users-container">
      <AdminSidebar />
      <div className="admin-content">
        <h1 className="admin-page-title">Mi Perfil</h1>
        
        <div className="profile-grid">
          {/* Columna Izquierda: Información de la cuenta */}
          <div className="profile-card">
            <div className="profile-header-accent"></div>
            <div className="profile-avatar-wrapper">
              {user?.avatar ? (
                <img 
                  src={`https://cdn.discordapp.com/avatars/${user.discord_id}/${user.avatar}.png`} 
                  alt="Discord Avatar" 
                  className="profile-avatar-img"
                  onError={(e) => {
                    e.target.onerror = null; 
                    e.target.src = '';
                  }}
                />
              ) : (
                <FaUser className="profile-avatar-placeholder" />
              )}
            </div>
            
            <h2 className="profile-username">{username || user?.username}</h2>
            <span className="profile-role-badge">{user?.role}</span>
            
            <div className="profile-info-list">
              <div className="profile-info-item">
                <div className="profile-info-icon"><FaUser /></div>
                <div className="profile-info-content">
                  <span className="profile-info-label">Usuario</span>
                  <span className="profile-info-value">{username || user?.username}</span>
                </div>
              </div>
              <div className="profile-info-item">
                <div className="profile-info-icon"><FaEnvelope /></div>
                <div className="profile-info-content">
                  <span className="profile-info-label">Email de contacto</span>
                  <span className="profile-info-value">{email || 'Sin configurar'}</span>
                </div>
              </div>
              <div className="profile-info-item">
                <div className="profile-info-icon"><FaShieldAlt /></div>
                <div className="profile-info-content">
                  <span className="profile-info-label">Rol Asignado</span>
                  <span className="profile-info-value" style={{ textTransform: 'capitalize' }}>{user?.role}</span>
                </div>
              </div>
              <div className="profile-info-item">
                <div className="profile-info-icon"><FaDiscord /></div>
                <div className="profile-info-content">
                  <span className="profile-info-label">Vinculación Discord</span>
                  {discordId ? (
                    <span className="discord-linked-badge">
                      <FaCheckCircle /> ID: {discordId}
                    </span>
                  ) : (
                    <span className="discord-unlinked-badge">
                      <FaExclamationTriangle /> No Vinculado
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Columna Derecha: Formulario de actualización */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 30 }}>
            <div className="admin-section" style={{ margin: 0 }}>
              <h2 className="section-title" style={{ fontSize: '1.6em', textAlign: 'left', marginBottom: 25, borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: 10 }}>
                Editar Información
              </h2>
              
              {success && (
                <div className="success-banner">
                  <FaCheckCircle /> {success}
                </div>
              )}
              {error && (
                <div className="error-banner">
                  <FaExclamationTriangle /> {error}
                </div>
              )}

              <form onSubmit={handleChangeProfile} className="profile-form-container">
                <div className="form-group">
                  <label>Nombre de Usuario:</label>
                  <div className="profile-input-group">
                    <input
                      type="text"
                      value={username}
                      onChange={e => setUsername(e.target.value)}
                      required
                    />
                    <FaUser className="profile-input-icon" />
                  </div>
                </div>

                <div className="form-group">
                  <label>Email:</label>
                  <div className="profile-input-group">
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                    />
                    <FaEnvelope className="profile-input-icon" />
                  </div>
                </div>

                <div className="form-group">
                  <label>Discord ID:</label>
                  <div className="profile-input-group">
                    <input
                      type="text"
                      value={discordId}
                      onChange={e => setDiscordId(e.target.value)}
                      placeholder="Ej: 485509123236954133"
                    />
                    <FaDiscord className="profile-input-icon" />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                  <div className="form-group">
                    <label>Nueva contraseña:</label>
                    <div className="profile-input-group">
                      <input
                        type="password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        placeholder="Mínimo 6 caracteres"
                      />
                      <FaLock className="profile-input-icon" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Repetir contraseña:</label>
                    <div className="profile-input-group">
                      <input
                        type="password"
                        value={password2}
                        onChange={e => setPassword2(e.target.value)}
                        placeholder="Confirmar contraseña"
                      />
                      <FaLock className="profile-input-icon" />
                    </div>
                  </div>
                </div>

                <div className="form-actions" style={{ marginTop: 10 }}>
                  <button type="submit" className="action-button primary-button" style={{ width: 'auto', padding: '12px 30px' }} disabled={loading}>
                    Guardar Cambios
                  </button>
                </div>
              </form>
            </div>

            {/* Zona Peligrosa */}
            <div className="admin-section danger-zone-card" style={{ margin: 0 }}>
              <div className="danger-zone-title">
                <FaExclamationTriangle /> Zona Peligrosa
              </div>
              <p className="danger-zone-desc">
                Al borrar tu cuenta perderás de forma permanente el acceso al panel de administración y todos los datos asociados. Esta acción no se puede deshacer.
              </p>
              <div className="form-actions" style={{ justifyContent: 'center' }}>
                <button
                  className="action-button delete-button"
                  style={{ width: '100%', maxWidth: 250 }}
                  onClick={handleDeleteAccount}
                  disabled={loading}
                >
                  <FaTrashAlt style={{ marginRight: 8 }} /> Eliminar Mi Cuenta
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AdminProfile;
